


import React, { useState } from 'react';
import { Upload, Modal, message } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';

const { Dragger } = Upload;

interface FileUploaderProps {
  mode: 'image-only' | 'image-and-pdf';
  imageField: string;
  pdfField?: string;
  using?:string // optional when image-only
}

const FileUploader: React.FC<FileUploaderProps> = ({ mode, imageField, pdfField,using }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);

  const imageFile = values[imageField];
  const pdfFile = pdfField ? values[pdfField] : null;

  const props = {
    name: 'file',
    showUploadList: false,
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';

      if (mode === 'image-only') {
        if (!isImage) {
          message.error('Only images are allowed.');
          return Upload.LIST_IGNORE;
        }
        setFieldValue(imageField, file);
      }

      if (mode === 'image-and-pdf') {
        if (isImage) {
          setFieldValue(imageField, file);
        } else if (isPDF) {
          if (!pdfField) return Upload.LIST_IGNORE;
          setFieldValue(pdfField, file);
        } else {
          message.error('Only images or PDFs are allowed.');
          return Upload.LIST_IGNORE;
        }
      }

      return false; // prevent automatic upload
    },
  };

  const handleRemove = (type: 'image' | 'pdf') => {
    if (type === 'image') setFieldValue(imageField, null);
    if (type === 'pdf' && pdfField) setFieldValue(pdfField, null);
  };

  const handlePreview = (file: File | string, type: 'image' | 'pdf') => {
    if (typeof file === 'string') {
      // existing URL, just show it
      setPreviewUrl(file);
      setPreviewType(type);
      setPreviewVisible(true);
    } else if (file instanceof File) {
      // create URL for preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewType(type);
      setPreviewVisible(true);
    }
  };

  const renderFilePreview = (file: File | string, type: 'image' | 'pdf') => (
    <div className="flex justify-between bg-[#f4f4f4] p-4 rounded-[16px] items-center gap-4 mt-4">
      {type === 'image' ? (
        <img
          src={typeof file === 'string' ? file : URL.createObjectURL(file)}
          alt="preview"
          className="w-10 h-10 object-cover rounded border border-gray-300 cursor-pointer"
          onClick={() => handlePreview(file, type)}
        />
      ) : (
        <span
          className="cursor-pointer underline"
          onClick={() => handlePreview(file, type)}
        >
          {typeof file === 'string' ? file.split('/').pop() : file.name}
        </span>
      )}
      <DeleteOutlined
        onClick={() => handleRemove(type)}
        className="text-xl cursor-pointer hover:text-red-500"
      />
    </div>
  );

  return (
    <>
      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {mode === 'image-only' ? 'Upload an Image' : 'Upload Image & PDF'}
        </p>
        { using!=='testimonial' && <p className="ant-upload-text">
         Image size must be exactly 350×250 pixels
        </p>}
      </Dragger>

      {imageFile && renderFilePreview(imageFile, 'image')}
      {mode === 'image-and-pdf' && pdfFile && renderFilePreview(pdfFile, 'pdf')}

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewUrl(null);
          setPreviewType(null);
        }}
        centered
        width={previewType === 'pdf' ? '80%' : undefined}
      >
        {previewType === 'image' && previewUrl && (
          <img alt="Full Preview" style={{ width: '100%' }} src={previewUrl} />
        )}
        {previewType === 'pdf' && previewUrl && (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </>
  );
};

export default FileUploader;

